import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { formatMoneyBR } from "../../utils/format";

type Option = { id: number; label: string };

type BudgetValue = {
  month: number;
  amount: number;
  adjustmentType?: "NONE" | "IPCA" | "IGPM";
};

type BudgetRevision = {
  id: number;
  year: number;
  revision: number;
  status: "DRAFT" | "CLOSED";
  values: BudgetValue[];
};

type BudgetPlan = {
  id: number;
  name: string;
  accountId: number | null;
  costCenterId: number | null;
  categoryId: number | null;
  account?: { id: number; name: string } | null;
  costCenter?: { id: number; name: string } | null;
  category?: { id: number; name: string } | null;
  justification: string | null;
  isActive?: boolean;
  currentRevision?: BudgetRevision | null;
};

type MonthItem = {
  month: number;
  amount: string;
  adjustmentType: "NONE" | "IPCA" | "IGPM";
};

type ActiveRevision = {
  year: number;
  revision: number;
  status: "DRAFT" | "CLOSED";
} | null;

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Marco" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const normalizeYearInput = (value: string) =>
  value.replace(/\D/g, "").slice(0, 4);

const isValidYear = (value: string) => {
  if (value.length !== 4) {
    return false;
  }
  const year = Number(value);
  if (Number.isNaN(year)) {
    return false;
  }
  const maxYear = new Date().getFullYear() + 15;
  return year <= maxYear;
};

const isMonthLocked = (year: number, month: number) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();
  if (year < currentYear) {
    return true;
  }
  if (year > currentYear) {
    return false;
  }
  const prevMonth = currentMonth - 1;
  const prevLocked = currentDay > 20;
  if (month < prevMonth) {
    return true;
  }
  if (month === prevMonth) {
    return prevLocked;
  }
  return false;
};

const buildMonthlyItems = (values: BudgetValue[] | undefined) => {
  const byMonth = new Map<number, BudgetValue>();
  (values ?? []).forEach((value) => {
    byMonth.set(value.month, value);
  });
  return Array.from({ length: 12 }, (_, idx) => {
    const month = idx + 1;
    const value = byMonth.get(month);
    return {
      month,
      amount: String(value?.amount ?? 0),
      adjustmentType: value?.adjustmentType ?? "NONE",
    } as MonthItem;
  });
};

export const BudgetsPage: React.FC = () => {
  const { client } = useAuth();
  const [budgets, setBudgets] = useState<BudgetPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState<BudgetPlan | null>(null);
  const [editingRevision, setEditingRevision] = useState<BudgetRevision | null>(
    null
  );
  const [currentStep, setCurrentStep] = useState<"basic" | "months">("basic");
  const [listYear, setListYear] = useState(
    new Date().getFullYear().toString()
  );
  const [activeRevision, setActiveRevision] = useState<ActiveRevision>(null);

  const [account, setAccount] = useState<Option | null>(null);
  const [costCenterId, setCostCenterId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [name, setName] = useState("");
  const [justification, setJustification] = useState("");

  const [monthlyItems, setMonthlyItems] = useState<MonthItem[]>([]);
  const [repeatValue, setRepeatValue] = useState("");
  const [costCenterOptions, setCostCenterOptions] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    Array<{ id: number; name: string }>
  >([]);

  const loadBudgets = useCallback(async () => {
    if (!isValidYear(listYear)) {
      setError("Ano invalido.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (listYear) {
        params.set("year", listYear);
      }
      const response = await client.get(
        `/modules/budgets?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error("Falha ao carregar orcamentos");
      }
      const data = (await response.json()) as { data: BudgetPlan[] };
      setBudgets(data.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }, [client, listYear]);

  const loadActiveRevision = useCallback(async () => {
    if (!isValidYear(listYear)) {
      setActiveRevision(null);
      return;
    }
    const response = await client.get(
      `/modules/budgets/revisions/active?year=${listYear}`
    );
    if (!response.ok) {
      setActiveRevision(null);
      return;
    }
    const payload = (await response.json()) as { data?: ActiveRevision };
    setActiveRevision(payload.data ?? null);
  }, [client, listYear]);

  const loadCostCenters = useCallback(async () => {
    const response = await client.get("/modules/cost-centers?take=200&skip=0");
    if (!response.ok) {
      return;
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    setCostCenterOptions(payload.data || []);
  }, [client]);

  const loadCategories = useCallback(async () => {
    const response = await client.get("/modules/categories?take=200&skip=0");
    if (!response.ok) {
      return;
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    setCategoryOptions(payload.data || []);
  }, [client]);

  useEffect(() => {
    void loadBudgets();
    void loadActiveRevision();
    void loadCostCenters();
    void loadCategories();
  }, [loadBudgets, loadActiveRevision, loadCostCenters, loadCategories]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const resetForm = () => {
    setAccount(null);
    setCostCenterId(null);
    setCategoryId(null);
    setYear(listYear || new Date().getFullYear().toString());
    setName("");
    setJustification("");
    setMonthlyItems(buildMonthlyItems());
    setRepeatValue("");
    setEditingBudget(null);
    setEditingRevision(null);
    setCurrentStep("basic");
    setError("");
  };

  const handleCreateNew = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (budget: BudgetPlan) => {
    setEditingBudget(budget);
    setEditingRevision(budget.currentRevision ?? null);
    setAccount(
      budget.accountId
        ? {
            id: budget.accountId,
            label: budget.account?.name ?? String(budget.accountId),
          }
        : null
    );
    setCostCenterId(budget.costCenterId ?? null);
    setCategoryId(budget.categoryId ?? null);
    setYear(String(budget.currentRevision?.year ?? listYear));
    setName(budget.name);
    setJustification(budget.justification ?? "");
    setMonthlyItems(buildMonthlyItems(budget.currentRevision?.values));
    setCurrentStep("basic");
    setShowModal(true);
  };

  const handleCreateRevisionForYear = async () => {
    if (!isValidYear(listYear)) {
      setError("Ano invalido.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const payload = {
        year: Number(listYear),
      };
      const response = await client.post(
        "/modules/budgets/revisions/by-year",
        payload
      );
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.error || "Falha ao criar revisao");
      }
      await loadBudgets();
      await loadActiveRevision();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar revisao");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseRevisionForYear = async () => {
    if (!isValidYear(listYear)) {
      setError("Ano invalido.");
      return;
    }
    if (!confirm("Deseja realmente fechar a revisao deste ano?")) return;
    setLoading(true);
    setError("");
    try {
      const response = await client.post(
        "/modules/budgets/revisions/by-year/close",
        { year: Number(listYear) }
      );
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.error || "Falha ao fechar revisao");
      }
      await loadBudgets();
      await loadActiveRevision();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fechar revisao");
    } finally {
      setLoading(false);
    }
  };

  const ensureRevisionForBudget = async (budgetId: number) => {
    const response = await client.get(
      `/modules/budgets/${budgetId}?year=${year}`
    );
    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      throw new Error(errorPayload?.error || "Falha ao carregar revisao ativa.");
    }
    const payload = (await response.json()) as { data?: BudgetPlan };
    if (!payload.data?.currentRevision) {
      throw new Error("Revisao ativa nao encontrada.");
    }
    setEditingBudget(payload.data);
    setEditingRevision(payload.data.currentRevision);
    setMonthlyItems(buildMonthlyItems(payload.data.currentRevision.values));
    return payload.data.currentRevision;
  };

  const handleNextStep = async () => {
    if (!account) {
      setError("Conta contabil e obrigatoria");
      return;
    }
    if (!isValidYear(year)) {
      setError("Ano invalido.");
      return;
    }
    if (!name.trim()) {
      setError("Nome/Descricao e obrigatorio");
      return;
    }
    try {
      if (!editingBudget?.id) {
        throw new Error("Salve o cadastro antes de continuar.");
      }
      if (!editingRevision?.id || editingRevision.year !== Number(year)) {
        await ensureRevisionForBudget(editingBudget.id);
      }
      setError("");
      setCurrentStep("months");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao abrir meses.");
    }
  };

  const handleMonthChange = (
    index: number,
    field: keyof MonthItem,
    value: MonthItem[keyof MonthItem]
  ) => {
    const updated = [...monthlyItems];
    updated[index] = { ...updated[index], [field]: value };
    setMonthlyItems(updated);
  };

  const handleRepeatValues = () => {
    const normalized = repeatValue.replace(",", ".");
    if (!normalized.trim() || isNaN(Number(normalized))) {
      setError("Informe um valor valido para repetir.");
      return;
    }
    setError("");
    setMonthlyItems((items) =>
      items.map((item) => ({
        ...item,
        amount: normalized,
      }))
    );
  };

  const calculateYearTotal = useMemo(() => {
    return monthlyItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
  }, [monthlyItems]);

  const handleSave = async () => {
    setError("");
    setLoading(true);

    try {
      if (!isValidYear(year)) {
        throw new Error("Ano invalido.");
      }
      const budgetPayload = {
        name,
        year: Number(year),
        accountId: account?.id || null,
        costCenterId,
        categoryId,
        justification: justification || null,
      };

      let budgetId = editingBudget?.id;
      if (!budgetId) {
        const response = await client.post("/modules/budgets", budgetPayload);
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.error || "Falha ao criar orcamento");
        }
        const data = (await response.json()) as { data: BudgetPlan };
        budgetId = data.data.id;
        if (data.data.currentRevision) {
          setEditingRevision(data.data.currentRevision);
        }
      } else {
        const response = await client.put(
          `/modules/budgets/${budgetId}`,
          budgetPayload
        );
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.error || "Falha ao atualizar orcamento");
        }
      }

      let revisionId = editingRevision?.id;
      if (!revisionId) {
        const ensured = await ensureRevisionForBudget(budgetId);
        revisionId = ensured.id;
      }

      const values = monthlyItems
        .filter((item) => !isMonthLocked(Number(year), item.month))
        .map((item) => ({
          month: item.month,
          amount: parseFloat(item.amount) || 0,
          adjustmentType: item.adjustmentType,
        }));

      const valuesResponse = await client.put(
        `/modules/budgets/revisions/${revisionId}/values`,
        { values }
      );
      if (!valuesResponse.ok) {
        const errorPayload = await valuesResponse.json().catch(() => null);
        throw new Error(
          errorPayload?.error || "Falha ao salvar valores do orcamento"
        );
      }

      setShowModal(false);
      resetForm();
      await loadBudgets();
      await loadActiveRevision();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar orcamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBasic = async () => {
    setError("");
    setLoading(true);
    try {
      if (!isValidYear(year)) {
        throw new Error("Ano invalido.");
      }
      const budgetPayload = {
        name,
        year: Number(year),
        accountId: account?.id || null,
        costCenterId,
        categoryId,
        justification: justification || null,
      };
      if (!editingBudget?.id) {
        const response = await client.post("/modules/budgets", budgetPayload);
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.error || "Falha ao criar orcamento");
        }
        const data = (await response.json()) as { data: BudgetPlan };
        setEditingBudget(data.data);
        setEditingRevision(data.data.currentRevision ?? null);
      } else {
        const response = await client.put(
          `/modules/budgets/${editingBudget.id}`,
          budgetPayload
        );
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.error || "Falha ao atualizar orcamento");
        }
      }
      await loadBudgets();
      await loadActiveRevision();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar cadastro");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Deseja realmente excluir este orcamento?")) return;

    setLoading(true);
    try {
      const response = await client.delete(`/modules/budgets/${id}`);
      if (!response.ok) {
        throw new Error("Falha ao excluir orcamento");
      }
      await loadBudgets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao excluir");
    } finally {
      setLoading(false);
    }
  };

  const isRevisionClosed = editingRevision?.status === "CLOSED";

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Orcamentos</h4>
        <button className="btn btn-primary" onClick={handleCreateNew}>
          + Novo Orcamento
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="d-flex align-items-end gap-2 mb-3">
        <div>
          <label className="form-label">Ano</label>
          <input
            className="form-control"
            value={listYear}
            onChange={(e) => setListYear(normalizeYearInput(e.target.value))}
          />
        </div>
        <button className="btn btn-outline-secondary" onClick={loadBudgets}>
          Atualizar
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={handleCreateRevisionForYear}
          disabled={loading || !isValidYear(listYear)}
        >
          Nova Revisao
        </button>
        <button
          className="btn btn-outline-dark"
          onClick={handleCloseRevisionForYear}
          disabled={loading || !activeRevision || activeRevision.status !== "DRAFT"}
        >
          Fechar Revisao
        </button>
        <div className="text-muted ms-2">
          {activeRevision
            ? `Revisao ${activeRevision.revision} (${activeRevision.status})`
            : "Sem revisao ativa"}
        </div>
      </div>

      {loading && <div className="text-center py-3">Carregando...</div>}

      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Ano</th>
              <th>Revisao</th>
              <th>Status</th>
              <th>Conta Contabil</th>
              <th>Centro de Custo</th>
              <th>Categoria</th>
              <th>Total Anual</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {budgets.map((budget) => {
              const revision = budget.currentRevision;
              const total =
                revision?.values?.reduce(
                  (sum, item) => sum + Number(item.amount || 0),
                  0
                ) || 0;
              return (
                <tr key={budget.id}>
                  <td>{budget.name}</td>
                  <td>{revision?.year ?? "-"}</td>
                  <td>{revision?.revision ?? "-"}</td>
                  <td>{revision?.status ?? "-"}</td>
                  <td>{budget.account?.name ?? "-"}</td>
                  <td>{budget.costCenter?.name ?? "-"}</td>
                  <td>{budget.category?.name ?? "-"}</td>
                  <td>{formatMoneyBR(total)}</td>
                  <td className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(budget)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(budget.id)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {currentStep === "basic"
                    ? "Orcamento - Dados Basicos"
                    : "Orcamento - Valores Mensais"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {currentStep === "basic" ? (
                  <>
                    <AutocompleteInput
                      label="Conta Contabil *"
                      placeholder="Buscar conta contabil"
                      value={account?.id ?? null}
                      selectedLabel={account?.label}
                      fetchOptions={fetchAccounts}
                      onSelect={setAccount}
                    />
                    <div className="mb-3">
                      <label className="form-label">Centro de Custo</label>
                      <select
                        className="form-select"
                        value={costCenterId ?? ""}
                        onChange={(e) =>
                          setCostCenterId(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      >
                        <option value="">Selecione</option>
                        {costCenterOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Categoria</label>
                      <select
                        className="form-select"
                        value={categoryId ?? ""}
                        onChange={(e) =>
                          setCategoryId(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      >
                        <option value="">Selecione</option>
                        {categoryOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Ano *</label>
                      <input
                        className="form-control"
                        value={year}
                        onChange={(e) => setYear(normalizeYearInput(e.target.value))}
                        disabled={Boolean(editingRevision)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Nome/Descricao *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Justificativa</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                      />
                    </div>
                    {editingRevision && (
                      <div className="alert alert-secondary">
                        Revisao {editingRevision.revision} -{" "}
                        {editingRevision.status}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="alert alert-info">
                      <strong>Total Anual: {formatMoneyBR(calculateYearTotal)}</strong>
                    </div>

                    <div className="row g-2 align-items-end mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Repetir valor</label>
                        <input
                          type="text"
                          className="form-control"
                          value={repeatValue}
                          onChange={(e) => setRepeatValue(e.target.value)}
                          placeholder="0,00"
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleRepeatValues}
                        >
                          Aplicar em todos os meses
                        </button>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Mes</th>
                            <th>Valor</th>
                            <th>Reajuste</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyItems.map((item, index) => {
                            const locked = isMonthLocked(Number(year), item.month);
                            const lockTitle = locked
                              ? "Mes encerrado, edicao bloqueada"
                              : undefined;
                            return (
                              <tr key={item.month}>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.month}
                                    onChange={(e) =>
                                      handleMonthChange(
                                        index,
                                        "month",
                                        Number(e.target.value)
                                      )
                                    }
                                    disabled={locked || isRevisionClosed}
                                    title={lockTitle}
                                  >
                                    {MONTHS.map((m) => (
                                      <option key={m.value} value={m.value}>
                                        {m.label}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={item.amount}
                                    onChange={(e) =>
                                      handleMonthChange(
                                        index,
                                        "amount",
                                        e.target.value
                                      )
                                    }
                                    disabled={locked || isRevisionClosed}
                                    title={lockTitle}
                                  />
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.adjustmentType}
                                    onChange={(e) =>
                                      handleMonthChange(
                                        index,
                                        "adjustmentType",
                                        e.target.value as MonthItem["adjustmentType"]
                                      )
                                    }
                                    disabled={locked || isRevisionClosed}
                                    title={lockTitle}
                                  >
                                    <option value="NONE">Sem reajuste</option>
                                    <option value="IPCA">IPCA</option>
                                    <option value="IGPM">IGPM</option>
                                  </select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {isRevisionClosed && (
                      <div className="alert alert-warning">
                        Revisao fechada. Crie uma nova revisao para editar.
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {currentStep === "basic" ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSaveBasic}
                      disabled={loading}
                    >
                      {loading ? "Salvando..." : "Salvar cadastro"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleNextStep}
                    >
                      Proximo
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setCurrentStep("basic")}
                    >
                      Voltar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={loading || isRevisionClosed}
                    >
                      {loading ? "Salvando..." : "Salvar Orcamento"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
